/**
 * ElencoDaExecucao — quem atua em cada etapa desta execução (SPEC A3 FR-10, FR-19, §6).
 *
 *   - DetalheDosNos: por nó do squad (`GET /executions/{id}/squad`), o tier efetivo, as
 *     etapas e o selo do prompt — só quando a API manda esses campos (execução anterior
 *     ao A3 não os tem, e a tela segue como antes).
 *   - ElencoDaExecucao: o elenco congelado (`GET /executions/{id}/agents`) com etapas,
 *     origem do responsável, versão do prompt, tier/modelo, perfil de ferramentas e as
 *     skills sem conteúdo em destaque.
 *
 * Paleta C, estilos inline, pt-BR.
 */
import { C } from '@/theme/palette';
import { Card, Badge } from '@/components/ui';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useExecutionAgents } from '@/api/executionAgents';
import { origemLegivel, TEXTO_DO_PERFIL } from '@/screens/journey/steps/squad/comum';
import {
  rotuloDaEtapa, inteiro, lista, TEXTO_DO_SELO_PROMPT, TEXTO_DA_ORIGEM,
} from '@/lib/agentesDaExecucao';

function Pilula({ cor, children, title, testId }) {
  return (
    <span
      data-testid={testId}
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600,
        padding: '2px 7px', borderRadius: 5, color: cor, background: `${cor}18`,
        border: `1px solid ${cor}55`, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

function Selo({ selo, title }) {
  if (!selo) return null;
  const cor = selo === 'especialista' ? C.cyber : C.warn;
  return <Pilula cor={cor} title={title}>{TEXTO_DO_SELO_PROMPT[selo] ?? selo}</Pilula>;
}

function Etapas({ etapas, origem }) {
  const lst = Array.isArray(etapas) ? etapas.filter(Boolean) : [];
  if (!lst.length) return <span style={{ color: C.neutralMuted }}>nenhuma etapa</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {lst.map((e) => {
        const o = origem && typeof origem === 'object' ? origem[e] : null;
        return (
          <Pilula key={e} cor={o === 'papel_padrao' ? C.warn : C.neutralDim}
            title={o ? `Responsável ${TEXTO_DA_ORIGEM[o] ?? o}` : undefined}>
            {rotuloDaEtapa(e)}{o ? ` · ${TEXTO_DA_ORIGEM[o] ?? o}` : ''}
          </Pilula>
        );
      })}
    </span>
  );
}

/** Campos do A3 num nó do squad — presentes só nas execuções novas. */
function temCamposA3(n) {
  return Boolean(n && (Array.isArray(n.etapas) || n.prompt || n.tool_profile));
}

export function DetalheDosNos({ nodes }) {
  const nos = lista(nodes).filter(temCamposA3);
  if (!nos.length) return null;
  return (
    <Card style={{ padding: 16, marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.neutral, marginBottom: 8 }}>
        Agentes do squad
      </div>
      {nos.map((n, i) => {
        const p = n.prompt || null;
        const titulo = p
          ? `Prompt ${origemLegivel(p.origem, p.version)} · ${inteiro(p.chars)} caracteres`
            + (p.hash ? ` · ${String(p.hash).slice(0, 12)}` : '')
          : undefined;
        return (
          <div key={n.node_id ?? `${n.agent_id}-${i}`} data-testid="no-do-squad"
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
              padding: '8px 0', borderTop: i ? `1px solid ${C.blackBorder}` : 'none',
              fontSize: 12.5 }}>
            <strong style={{ color: C.neutral, minWidth: 160 }}>
              {n.node_label || n.agent_id || '—'}
            </strong>
            {n.tier ? <Badge color={C.cyber} light>{n.tier}</Badge> : null}
            {p ? <Selo selo={p.selo} title={titulo} /> : null}
            {p ? (
              <span style={{ color: C.neutralMuted, fontSize: 11.5 }}>
                prompt {origemLegivel(p.origem, p.version)}
              </span>
            ) : null}
            <Etapas etapas={n.etapas} origem={n.origem_responsavel} />
          </div>
        );
      })}
    </Card>
  );
}

function SkillsDoAgente({ skills }) {
  const lst = lista(skills);
  if (!lst.length) return <span style={{ color: C.neutralMuted }}>nenhuma</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {lst.map((s) => (s.tem_conteudo === false ? (
        <Pilula key={s.name} cor={C.warn} testId={`skill-sem-conteudo-${s.name}`}>
          ⚠️ skill sem conteúdo: {s.name}
        </Pilula>
      ) : (
        <Pilula key={s.name} cor={C.neutralDim}>{s.name}</Pilula>
      )))}
    </span>
  );
}

function selo(agente) {
  if (agente.status_catalogo === 'rascunho' || agente.status_catalogo === 'ausente'
      || agente.prompt_origem === 'sem_prompt') return 'sem_prompt';
  return null;
}

export function ElencoDaExecucao({ executionId }) {
  const { data, isLoading, error, refetch } = useExecutionAgents(executionId);
  const agentes = lista(data);

  return (
    <Card style={{ padding: 16, marginTop: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
        Elenco da execução
      </div>
      <div style={{ fontSize: 12, color: C.neutralMuted, marginBottom: 10 }}>
        Congelado no início da execução: quem faz cada etapa, com qual prompt, tier e
        ferramentas. Mudar o squad depois não altera esta execução.
      </div>
      {isLoading ? <LoadingSpinner label="Carregando elenco..." /> : null}
      {error ? <QueryError error={error} onRetry={() => refetch()} /> : null}
      {!isLoading && !error && !agentes.length ? (
        <div style={{ fontSize: 12.5, color: C.neutralMuted }}>
          Elenco ainda não congelado para esta execução.
        </div>
      ) : null}
      {agentes.map((a, i) => {
        const s = selo(a);
        return (
          <div key={`${a.agent_name}-${i}`} data-testid="membro-do-elenco"
            style={{ padding: '10px 0', borderTop: `1px solid ${C.blackBorder}`,
              display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12.5 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <strong style={{ color: C.neutral }}>{a.node_label || a.agent_name}</strong>
              {a.node_label && a.node_label !== a.agent_name ? (
                <span style={{ color: C.neutralMuted, fontFamily: 'monospace', fontSize: 11 }}>
                  {a.agent_name}
                </span>
              ) : null}
              <Badge color={C.cyber} light>{a.tier || '—'}</Badge>
              {a.resolved_model ? (
                <span style={{ color: C.neutralMuted, fontSize: 11 }}>{a.resolved_model}</span>
              ) : null}
              {s ? <Selo selo={s} /> : null}
              {a.status_catalogo && a.status_catalogo !== 'ativo' ? (
                <Pilula cor={C.warn}>{a.status_catalogo}</Pilula>
              ) : null}
            </div>
            <div style={{ color: C.neutralDim }}>
              Prompt {origemLegivel(a.prompt_origem, a.prompt_version)}
              {a.prompt_version != null ? ` (versão ${a.prompt_version})` : ''}
              {' · '}{inteiro(a.prompt_chars)} caracteres
              {a.tool_profile ? (
                <>
                  {' · '}ferramentas: <span style={{ color: C.neutral }}>{a.tool_profile}</span>
                  {TEXTO_DO_PERFIL[a.tool_profile] ? ` (${TEXTO_DO_PERFIL[a.tool_profile]})` : ''}
                </>
              ) : null}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ color: C.neutralMuted, fontSize: 11.5 }}>Etapas:</span>
              <Etapas etapas={a.etapas} origem={a.origem_responsavel} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              <span style={{ color: C.neutralMuted, fontSize: 11.5 }}>Skills:</span>
              <SkillsDoAgente skills={a.skills} />
            </div>
          </div>
        );
      })}
    </Card>
  );
}

export default ElencoDaExecucao;
